import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useAuth } from "../context/AuthContext";
import { PageHeader } from "../components/common";
import { api } from "../lib/api";
import { Button } from "../components/ui/button";
import { toast } from "sonner";
import { Eye, ArrowRight, Sparkles, Lock } from "lucide-react";

export default function SettingsPage() {
  const { user } = useAuth();
  const [nyaya, setNyaya] = useState(null);
  const [busy, setBusy] = useState(false);

  const loadStatus = async () => {
    try {
      const { data } = await api.get("/nyaya/status");
      setNyaya(data);
    } catch {
      setNyaya({ enabled: false });
    }
  };
  useEffect(() => { loadStatus(); }, []);

  const toggle = async () => {
    setBusy(true);
    try {
      const path = nyaya?.enabled ? "/nyaya/disable" : "/nyaya/enable";
      const { data } = await api.post(path);
      setNyaya(data);
      toast.success(data.enabled ? "Nyaya Drishti enabled." : "Nyaya Drishti disabled.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Could not update add-on.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div data-testid="settings-page">
      <PageHeader eyebrow="Le Profil" title="Settings" description="Your account, your workspace." />
      <div className="grid lg:grid-cols-2 gap-6">
        <div className="max-w-lg border border-[#E8E3F0] bg-white p-6 space-y-4">
          <div>
            <div className="label-eyebrow mb-1">Name</div>
            <div className="font-serif text-2xl">{user?.full_name}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Email</div>
            <div>{user?.email}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Role</div>
            <div className="capitalize">{user?.role}</div>
          </div>
          <div>
            <div className="label-eyebrow mb-1">Member since</div>
            <div>{user?.created_at && new Date(user.created_at).toLocaleDateString()}</div>
          </div>
        </div>

        {/* Nyaya Drishti add-on */}
        <div className="border border-[#7B56C9] bg-white p-6" data-testid="settings-nyaya-card">
          <div className="flex items-start gap-3 mb-4">
            <Eye size={22} strokeWidth={1.4} className="text-[#7B56C9] mt-1" />
            <div>
              <div className="label-eyebrow text-[#C5A059] mb-1">Add-on · ₹1,200 / month</div>
              <div className="font-serif text-2xl leading-tight">Nyaya Drishti</div>
              <div className="text-sm text-[#2A0F5A]/75 mt-1">
                Vision-based legal filing detection. Snap a photo — Marianne extracts the case
                number, parties, court, dates and flags defects automatically.
              </div>
            </div>
          </div>

          <div className="flex items-center justify-between border-t border-[#E8E3F0] pt-4">
            <div className="flex items-center gap-2 text-sm">
              <Lock size={14} className="text-[#7B56C9]" />
              <span>
                Status:{" "}
                <strong className={nyaya?.enabled ? "text-[#2A0F5A]" : "text-[#4A5568]"}>
                  {nyaya === null ? "…" : nyaya.enabled ? "Enabled" : "Disabled"}
                </strong>
              </span>
            </div>
            <Button
              onClick={toggle}
              disabled={busy || nyaya === null}
              className={`rounded-none ${
                nyaya?.enabled
                  ? "bg-white border border-[#2A0F5A] text-[#2A0F5A] hover:bg-[#FAFAF8]"
                  : "bg-[#2A0F5A] hover:bg-[#3D1F85] text-[#FAFAF8]"
              }`}
              data-testid="settings-nyaya-toggle"
            >
              {nyaya?.enabled ? "Disable add-on" : (<><Sparkles size={14} className="mr-1.5" /> Enable add-on</>)}
            </Button>
          </div>

          {nyaya?.enabled && (
            <Link
              to="/app/nyaya"
              className="mt-4 inline-flex items-center gap-1.5 text-sm text-[#7B56C9] hover:text-[#2A0F5A]"
              data-testid="settings-nyaya-open"
            >
              Open Nyaya Drishti workspace <ArrowRight size={14} />
            </Link>
          )}
        </div>
      </div>
    </div>
  );
}
